import React, { useCallback, useEffect, useState } from 'react';
import {
  FaClipboardList,
  FaExclamationTriangle,
  FaHistory,
  FaSpinner,
} from 'react-icons/fa';
import Seo from '../components/Seo';
import { getVerificationStatusMeta } from '../utils/verificationStatus';
import './VerificationHistoryPage.css';

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'paid', label: 'Paid' },
  { id: 'unpaid', label: 'Unpaid' },
  { id: 'awaiting-review', label: 'Awaiting review' },
  { id: 'pending', label: 'Pending notification' },
  { id: 'notification-sent', label: 'Notification sent' },
  { id: 'rejected', label: 'Rejected' },
  { id: 'refunded', label: 'Refunded' },
  { id: 'failed', label: 'Failed' },
];

function formatAmount(item) {
  const amount = item.amountPaidInr ?? item.amountExpectedInr;
  if (amount == null) return '—';
  return `₹${amount.toLocaleString('en-IN')}`;
}

function formatCheckedAt(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function getStatusMeta(item) {
  return getVerificationStatusMeta(item);
}

function matchesClientFilter(item, filter) {
  const meta = getStatusMeta(item);
  if (filter === 'awaiting-review') return meta.className === 'status-review-pending';
  if (filter === 'rejected') return meta.className === 'status-rejected';
  if (filter === 'pending') return meta.className === 'status-pending';
  if (filter === 'notification-sent') return meta.className === 'status-notified';
  if (filter === 'paid') return item.paymentStatus === 'PAID';
  if (filter === 'unpaid') return item.paymentStatus === 'UNPAID';
  if (filter === 'refunded') return item.paymentStatus === 'REFUNDED';
  if (filter === 'failed') return item.verificationStatus === 'FAILED';
  return true;
}

function formatCheckType(checkType) {
  if (checkType === 'NOMINEE_LOOKUP') return 'Nominee lookup';
  return 'Notify all nominees';
}

function getContactLabel(item) {
  if (item.checkType === 'NOMINEE_LOOKUP') {
    return item.targetNomineeEmail || item.targetNomineePhone || '—';
  }
  return item.policyholderEmail || item.policyholderPhone || '—';
}

function VerificationHistoryPage({ user, onNavigateNomineeCheck }) {
  const [filter, setFilter] = useState('all');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchHistory = useCallback(async () => {
    const token = localStorage.getItem('token');
    setLoading(true);
    setError('');

    try {
      const reviewFilters = ['awaiting-review', 'rejected'];
      const queryParam = reviewFilters.includes(filter)
        ? `reviewStatus=${filter === 'awaiting-review' ? 'PENDING_REVIEW' : 'REJECTED'}`
        : `paymentStatus=${filter}`;

      const response = await fetch(
        `${process.env.REACT_APP_HOST_SERVER}/api/nominee-verifications?${queryParam}&page=1&limit=50`,
        {
          headers: {
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        }
      );

      if (!response.ok) {
        throw new Error('Unable to load verification history.');
      }

      const json = await response.json();
      if (!json.successful) {
        throw new Error(json.message || 'Unable to load verification history.');
      }

      let nextItems = json.data?.items || [];
      if (reviewFilters.includes(filter) && nextItems.length === 0) {
        const fallback = await fetch(
          `${process.env.REACT_APP_HOST_SERVER}/api/nominee-verifications?paymentStatus=all&page=1&limit=50`,
          { headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) } }
        );
        const fallbackJson = await fallback.json();
        if (fallback.ok && fallbackJson.successful) {
          nextItems = (fallbackJson.data?.items || []).filter((item) => matchesClientFilter(item, filter));
        }
      }

      setItems(nextItems);
    } catch (err) {
      setItems([]);
      setError(err.message || 'Unable to load verification history.');
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    if (user) {
      fetchHistory();
    } else {
      setLoading(false);
      setItems([]);
    }
  }, [user, fetchHistory]);

  return (
    <>
      <Seo
        title="Verification History"
        description="View your Verify and Notify Nominees history on KeepMyAsset."
        path="/verification-history"
        noindex
      />
      <div className="verification-history-page">
        <header className="verification-history-header">
          <div>
            <p className="verification-history-eyebrow">Verify &amp; Notify Nominees</p>
            <h1>
              <FaHistory aria-hidden="true" />
              Verification History
            </h1>
            <p className="verification-history-subtitle">
              Track every verification you start — including secure-mode requests awaiting manual review, approvals, rejections, and notifications sent.
            </p>
          </div>
          <button type="button" className="verification-history-cta" onClick={onNavigateNomineeCheck}>
            Start new verification
          </button>
        </header>

        <div className="verification-history-filters">
          <label className="verification-history-filter-select-wrap" htmlFor="verification-history-filter-select">
            <span className="verification-history-filter-select-label">Status</span>
            <select
              id="verification-history-filter-select"
              className="verification-history-filter-select"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              aria-label="Filter verification history"
            >
              {FILTERS.map(({ id, label }) => (
                <option key={id} value={id}>
                  {label}
                </option>
              ))}
            </select>
          </label>

          <div className="verification-history-filter-pills" role="tablist" aria-label="Filter verification history">
            {FILTERS.map(({ id, label }) => (
              <button
                key={id}
                type="button"
                role="tab"
                aria-selected={filter === id}
                className={`verification-history-filter ${filter === id ? 'active' : ''}`}
                onClick={() => setFilter(id)}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="verification-history-state">
            <FaSpinner className="spin-icon" aria-hidden="true" />
            <p>Loading verification history…</p>
          </div>
        ) : error ? (
          <div className="verification-history-state verification-history-state-error">
            <FaExclamationTriangle aria-hidden="true" />
            <p>{error}</p>
            <button type="button" onClick={fetchHistory}>Try again</button>
          </div>
        ) : items.length === 0 ? (
          <div className="verification-history-state">
            <FaClipboardList aria-hidden="true" />
            <p>No verification records found for this filter.</p>
            <button type="button" className="verification-history-cta" onClick={onNavigateNomineeCheck}>
              Verify &amp; notify nominees
            </button>
          </div>
        ) : (
          <>
            <div className="verification-history-table-wrap">
              <table className="verification-history-table">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Email / Phone</th>
                    <th>Amount</th>
                    <th>Checked at</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => {
                    const status = getStatusMeta(item);
                    const StatusIcon = status.icon;
                    return (
                      <tr key={item.id}>
                        <td data-label="Type">{formatCheckType(item.checkType)}</td>
                        <td data-label="Email / Phone">{getContactLabel(item)}</td>
                        <td data-label="Amount">{formatAmount(item)}</td>
                        <td data-label="Checked at">{formatCheckedAt(item.checkedAt)}</td>
                        <td data-label="Status">
                          <span className={`verification-status-badge ${status.className}`}>
                            <StatusIcon aria-hidden="true" />
                            {status.label}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="verification-history-cards">
              {items.map((item) => {
                const status = getStatusMeta(item);
                const StatusIcon = status.icon;
                return (
                  <article key={item.id} className="verification-history-card">
                    <div className="verification-history-card-top">
                      <div>
                        <strong>{getContactLabel(item)}</strong>
                        <p className="verification-history-card-type">{formatCheckType(item.checkType)}</p>
                      </div>
                      <span className={`verification-status-badge ${status.className}`}>
                        <StatusIcon aria-hidden="true" />
                        {status.label}
                      </span>
                    </div>
                    <dl>
                      <div>
                        <dt>Amount</dt>
                        <dd>{formatAmount(item)}</dd>
                      </div>
                      <div>
                        <dt>Checked at</dt>
                        <dd>{formatCheckedAt(item.checkedAt)}</dd>
                      </div>
                    </dl>
                    {item.failureReason ? (
                      <p className="verification-history-failure">{item.failureReason}</p>
                    ) : null}
                  </article>
                );
              })}
            </div>
          </>
        )}
      </div>
    </>
  );
}

export default VerificationHistoryPage;
